import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { ActiveElement, Chart, ChartEvent } from 'chart.js';
import { baseOptions, drawSpark, useChart } from './charts';
import type { Ctx } from './Dashboard';
import { expenses, LedgerFilter, range, revenue } from './data';
import { ddmmyyyy, lastDay, ml, mlf, pct, rub, sum, WD } from './fmt';

interface Day { day: string; cash: number; bank: number }

export function Overview({ ctx }: { ctx: Ctx }) {
  const { series: s, meta, from, to, toggles: t, le, drill } = ctx;
  const M = s.months;
  const I = range(from, to);
  const n = I.length;
  const P = from - n >= 0 ? range(from - n, from - 1) : null;
  const rev = (i: number) => revenue(s, i);
  const exp = (i: number) => expenses(s, i, t);
  const payroll = s.costs.filter((c) => c.payroll);
  const owner = s.costs.find((c) => c.special === 'owner')!;
  const capex = s.costs.filter((c) => c.special === 'capex');
  const agg = (J: number[]) => {
    const rv = sum(J.map(rev));
    const ox = sum(J.map(exp));
    return {
      rv, ox, pr: rv - ox,
      cash: sum(J.map((i) => s.cash[i])),
      own: sum(J.map((i) => owner.values[i])),
      cap: sum(J.map((i) => sum(capex.map((c) => c.values[i])))),
      fot: sum(J.map((i) => sum(payroll.map((c) => c.values[i])))),
    };
  };
  const a = agg(I);
  const p = P ? agg(P) : null;
  const period: LedgerFilter = { from: M[from], to: M[to] };
  const dl = (x: number, y: number | undefined, inv = false) => {
    if (!p || !y) return <span>нет базы для сравнения</span>;
    const d = (x - y) / Math.abs(y);
    return (
      <span>
        <span className={(inv ? d < 0 : d > 0) ? 'up' : 'dn'}>{d > 0 ? '+' : ''}{pct(d)}</span> к пред. {n} мес.
      </span>
    );
  };

  type Kpi = [string, string, React.ReactNode, number[], string, LedgerFilter | null];
  const kpis: Kpi[] = [
    ['Выручка', rub(a.rv), dl(a.rv, p?.rv), I.map(rev), '--c1', { type: 'inc' }],
    ['Расходы', rub(a.ox), dl(a.ox, p?.ox, true), I.map(exp), '--c7', { type: 'exp' }],
    ['Прибыль', rub(a.pr), <>маржа {pct(a.pr / a.rv)}{p ? ` · было ${pct(p.pr / p.rv)}` : ''}</>, I.map((i) => rev(i) - exp(i)), '--good', null],
    ['Доля наличных', pct(a.cash / a.rv), `${rub(a.cash)} наличными`, I.map((i) => s.cash[i] / (rev(i) || 1)), '--c4', { type: 'inc', line: 'rev_cash' }],
  ];
  if (payroll.length) {
    kpis.push(['ФОТ / выручка', pct(a.fot / a.rv), rub(a.fot), I.map((i) => sum(payroll.map((c) => c.values[i])) / (rev(i) || 1)), '--c3',
      payroll.length === 1 ? { type: 'exp', group: payroll[0].key } : { type: 'exp' }]);
  }
  kpis.push(['Выплаты собственнику', rub(a.own), `CAPEX ${rub(a.cap)}`, I.map((i) => owner.values[i]), '--berry', { line: 'owner' }]);

  const open = M[to] === meta.today.slice(0, 7);
  const [dayMonth, setDayMonth] = useState(M[open && to > from ? to - 1 : to]);
  const lab = I.map((i) => ml(M[i]));

  const main = useChart((T) => ({
    type: 'bar',
    data: {
      labels: lab,
      datasets: [
        { label: 'Прибыль', type: 'line', data: I.map((i) => rev(i) - exp(i)), borderColor: T.good, backgroundColor: T.good, borderWidth: 2, pointRadius: 3, pointHoverRadius: 5, order: 0 },
        { label: 'Выручка', data: I.map(rev), backgroundColor: T.c[0], borderRadius: 4, maxBarThickness: 26, order: 1 },
        { label: 'Расходы', data: I.map(exp), backgroundColor: T.c[6], borderRadius: 4, maxBarThickness: 26, order: 1 },
      ],
    },
    options: {
      ...baseOptions(T, { autoSkip: true }),
      onClick: (_e: ChartEvent, _el: ActiveElement[], ch: Chart) => {
        const x = ch.scales.x.getValueForPixel((_e as unknown as { x: number }).x);
        if (x != null && x >= 0 && x < I.length) setDayMonth(M[I[x]]);
      },
      onHover: (e: ChartEvent, el: ActiveElement[]) => {
        const target = (e.native?.target as HTMLElement | undefined);
        if (target) target.style.cursor = el.length ? 'pointer' : 'default';
      },
    },
  }) as never, [from, to, t.own, t.cap, s]);

  const mix = useChart((T) => ({
    type: 'bar',
    data: {
      labels: lab,
      datasets: [
        { label: 'Наличные', data: I.map((i) => s.cash[i]), backgroundColor: T.c[3], maxBarThickness: 20, fmt: (v: number, k: number) => `${rub(v)} · ${pct(v / (rev(I[k]) || 1))}` },
        { label: 'Безнал', data: I.map((i) => s.bank[i] + s.other[i]), backgroundColor: T.c[0], borderRadius: { topLeft: 4, topRight: 4 }, maxBarThickness: 20 },
      ],
    },
    options: baseOptions(T, { stack: true, autoSkip: true }),
  }) as never, [from, to, s]);

  return (
    <>
      <section className="kpis">
        {kpis.map((k) => (
          <KpiCard key={k[0]} k={k} onClick={k[5] ? () => drill({ ...period, ...k[5] }, 'overview') : undefined} />
        ))}
      </section>
      <section className="grid">
        <div className="card s8">
          <div className="hd">
            <div>
              <h3>Выручка, расходы и прибыль</h3>
              <p className="sub">Нажмите на месяц — откроется его дневная выручка ниже</p>
            </div>
            <Legend items={[['--c1', 'Выручка'], ['--c7', 'Расходы'], ['--good', 'Прибыль']]} />
          </div>
          <div className="ch" style={{ height: 300 }}>
            <canvas ref={main} role="img" aria-label="Выручка, расходы и прибыль по месяцам" />
          </div>
          {open && (
            <p className="note">
              <b>{mlf(M[to])}</b> ещё идёт: часть расходов месяца (зарплата, налоги, лаборатория) оплачивается позже, прибыль пока завышена.
            </p>
          )}
          {s.costs.some((c) => c.key === '_unmapped' && sum(I.map((i) => c.values[i])) > 0) && (
            <p className="note">
              Есть расходы без статьи: финансисту нужно проверить соответствия в МИС (Справочники → Соответствия).
            </p>
          )}
        </div>
        <div className="card s4">
          <h3>Наличные и безнал</h3>
          <p className="sub">Структура прихода по способу оплаты</p>
          <Legend items={[['--c4', 'Наличные'], ['--c1', 'Безнал']]} />
          <div className="ch" style={{ height: 260 }}>
            <canvas ref={mix} role="img" aria-label="Наличные и безналичная выручка" />
          </div>
        </div>
        <DailyCard ctx={ctx} month={dayMonth} setMonth={setDayMonth} le={le} />
        <WeekdayCard ctx={ctx} />
      </section>
    </>
  );
}

function KpiCard({ k, onClick }: { k: [string, string, React.ReactNode, number[], string, LedgerFilter | null]; onClick?: () => void }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const T = getComputedStyle(ref.current!).getPropertyValue(k[4]).trim();
    drawSpark(ref.current, k[3], T);
  });
  return (
    <button className="kpi" type="button" disabled={!onClick} onClick={onClick}>
      <span className="l">{k[0]}</span>
      <span className="v">{k[1]}</span>
      <span className="d">{k[2]}</span>
      <canvas ref={ref} aria-hidden="true" />
    </button>
  );
}

export function Legend({ items }: { items: [string, string][] }) {
  return (
    <div className="leg">
      {items.map(([c, t]) => (
        <span key={t}>
          <i style={{ background: `var(${c})` }} />
          {t}
        </span>
      ))}
    </div>
  );
}

function DailyCard({ ctx, month, setMonth, le }: { ctx: Ctx; month: string; setMonth: (m: string) => void; le: string | null }) {
  const M = ctx.series.months;
  const [days, setDays] = useState<Day[] | null>(null);
  useEffect(() => {
    setDays(null);
    const q = new URLSearchParams({ from: `${month}-01`, to: lastDay(month) });
    if (le) q.set('le', le);
    api.get<{ rows: Day[] }>(`/dashboard/daily?${q}`).then((r) => setDays(r.rows), () => setDays([]));
  }, [month, le]);
  const nd = +lastDay(month).slice(8);
  const ca = Array<number>(nd).fill(0);
  const cd = Array<number>(nd).fill(0);
  for (const d of days ?? []) {
    ca[+d.day.slice(8) - 1] += d.cash;
    cd[+d.day.slice(8) - 1] += d.bank;
  }
  const tot = ca.map((v, i) => v + cd[i]);
  const filled = tot.filter((v) => v > 0);
  const chart = useChart((T) => (days ? {
    type: 'bar',
    data: {
      labels: tot.map((_, i) => i + 1),
      datasets: [
        { label: 'Наличные', data: ca, backgroundColor: T.c[3], maxBarThickness: 16 },
        { label: 'Безнал', data: cd, backgroundColor: T.c[0], borderRadius: { topLeft: 3, topRight: 3 }, maxBarThickness: 16 },
      ],
    },
    options: {
      ...baseOptions(T, { stack: true, autoSkip: true }),
      onClick: (_e: ChartEvent, el: ActiveElement[]) => {
        if (el.length) {
          const d = `${month}-${String(el[0].index + 1).padStart(2, '0')}`;
          ctx.drill({ from: month, to: month, day: d, type: 'inc' }, 'overview');
        }
      },
    },
  } : null) as never, [days, month]);
  return (
    <div className="card s8">
      <div className="hd">
        <div>
          <h3>Дневная выручка</h3>
          <p className="sub">По дате оплаты. Нажмите на день — откроются его операции</p>
        </div>
        <select aria-label="Месяц" value={month} onChange={(e) => setMonth(e.target.value)}>
          {M.map((m) => <option key={m} value={m}>{mlf(m)}</option>)}
        </select>
      </div>
      <Legend items={[['--c4', 'Наличные'], ['--c1', 'Безнал']]} />
      <div className="ch" style={{ height: 220 }}>
        <canvas ref={chart} role="img" aria-label="Выручка по дням месяца" />
      </div>
      <p className="note">
        Дней с выручкой: <b>{filled.length}</b> · в среднем <b>{rub(sum(filled) / (filled.length || 1))}</b> · максимум{' '}
        <b>{rub(Math.max(0, ...tot))}</b>.{' '}
        <button className="btn sm" type="button" onClick={() => ctx.drill({ from: month, to: month }, 'overview')}>
          Операции за {ml(month)} →
        </button>
      </p>
    </div>
  );
}

function WeekdayCard({ ctx }: { ctx: Ctx }) {
  const { series, from, to, le } = ctx;
  const M = series.months;
  const [days, setDays] = useState<Day[] | null>(null);
  useEffect(() => {
    const q = new URLSearchParams({ from: `${M[from]}-01`, to: lastDay(M[to]) });
    if (le) q.set('le', le);
    api.get<{ rows: Day[] }>(`/dashboard/daily?${q}`).then((r) => setDays(r.rows), () => setDays([]));
  }, [M, from, to, le]);
  const avg = useMemo(() => {
    const wd = [0, 0, 0, 0, 0, 0, 0];
    const wn = [0, 0, 0, 0, 0, 0, 0];
    for (const d of days ?? []) {
      const v = d.cash + d.bank;
      if (v <= 0) continue;
      const w = (new Date(`${d.day}T00:00:00`).getDay() + 6) % 7;
      wd[w] += v;
      wn[w] += 1;
    }
    return { wd, wn };
  }, [days]);
  const chart = useChart((T) => (days ? {
    type: 'bar',
    data: {
      labels: WD,
      datasets: [{
        label: 'Средняя выручка',
        data: avg.wd.map((v, i) => (avg.wn[i] ? v / avg.wn[i] : 0)),
        backgroundColor: avg.wd.map((_, i) => (i >= 5 ? T.c[1] : T.c[0])),
        borderRadius: 4, maxBarThickness: 28,
        fmt: (v: number, i: number) => `${rub(v)} · дней: ${avg.wn[i]}`,
      }],
    },
    options: baseOptions(T),
  } : null) as never, [avg, days]);
  return (
    <div className="card s4">
      <h3>По дням недели</h3>
      <p className="sub">Средняя выручка за день с выручкой, выбранный период</p>
      <div className="ch" style={{ height: 220 }}>
        <canvas ref={chart} role="img" aria-label="Средняя выручка по дням недели" />
      </div>
      {days && days.length > 0 && (
        <p className="note">
          С {ddmmyyyy(days[0].day)} по {ddmmyyyy(days[days.length - 1].day)}
        </p>
      )}
    </div>
  );
}

